import { useMemo } from 'react'
import { resourceManager } from '@/Core/Managers/Resource'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

import { LIST_ORDER } from '../Contracts/ListGroups'

/**
 * Конфігурація ресурсів списку для меню
 */
export interface ListResourceMenuItem {
    /** Ключ ресурсу */
    key: string
    /** Заголовок для відображення */
    title: string
    /** Шлях до сторінки */
    path: string
    /** Повна адреса сторінки: не всі довідники живуть під `/settings/` */
    pageUrl: string
}

/**
 * Довідники, ресурс яких не має префікса `Setting.List`: довідник за
 * призначенням, але ресурс живе в іншій секції. Тут перелічують лише КЛЮЧ —
 * імпорту між контейнерами не виникає.
 */
const EXTERNAL_LIST_RESOURCES: readonly string[] = []

/**
 * Хук для отримання динамічного списку ресурсів Setting.List*
 *
 * @param prefix - префікс для фільтрації ресурсів (за замовчуванням 'Setting.List')
 * @returns масив ресурсів для меню
 *
 * @example
 * ```tsx
 * const listResources = useListResources()
 * // Повертає масив з усіма ресурсами Setting.List* у форматі:
 * // [
 * //   { key: 'Setting.ListCurrency', title: 'Валюти', path: 'currencies' },
 * //   { key: 'Setting.ListCurrencyRate', title: 'Курси валют', path: 'currency-rates' }
 * // ]
 * ```
 */
export const useListResources = (prefix: string = 'Setting.List'): ListResourceMenuItem[] => {
    const listResources = useMemo(() => {
        const allResources = resourceManager.getAll()
        const result: ListResourceMenuItem[] = []

        // Фільтруємо ресурси за префіксом
        Object.entries(allResources).forEach(([key, resource]: [string, IResourceConfig]) => {
            if (!key.startsWith(prefix) && !EXTERNAL_LIST_RESOURCES.includes(key)) {
                return
            }

            // Перевіряємо наявність необхідних полів
            if (!resource.title || !resource.pageUrl) {
                console.warn(`[useListResources] Ресурс ${key} не має title або pageUrl`, resource)
                return
            }

            // Витягуємо шлях з pageUrl
            // pageUrl має формат '/settings/currencies', нам потрібен 'currencies'
            const pathParts = resource.pageUrl.split('/')
            const path = pathParts[pathParts.length - 1]

            if (!path) {
                console.warn(`[useListResources] Не вдалося витягти path з pageUrl для ресурсу ${key}`, resource)
                return
            }

            result.push({
                key,
                title: resource.title,
                path,
                pageUrl: resource.pageUrl,
            })
        })

        // Порядок — як на сторінці «Довідники» (`LIST_GROUPS`, `SPEC-007` §7.3):
        // горизонтальне меню над довідником читається тією самою послідовністю.
        // Нерозкладені (їх ловить вартовий груп) — у кінці, за абеткою
        const position = (key: string): number => {
            const index = LIST_ORDER.indexOf(key)

            return index === -1 ? Number.MAX_SAFE_INTEGER : index
        }

        result.sort((a, b) => position(a.key) - position(b.key) || a.title.localeCompare(b.title, 'uk'))

        return result
    }, [prefix])

    return listResources
}

/**
 * Хук для отримання масиву ключів ресурсів для useMenuNavigation
 *
 * @param prefix - префікс для фільтрації ресурсів (за замовчуванням 'Setting.List')
 * @returns масив ключів ресурсів
 *
 * @example
 * ```tsx
 * const menuItemsBase = useListResourceKeys()
 * // Повертає: ['Setting.ListCurrency', 'Setting.ListCurrencyRate']
 * ```
 */
export const useListResourceKeys = (prefix: string = 'Setting.List'): string[] => {
    const listResources = useListResources(prefix)
    return useMemo(() => listResources.map((r) => r.key), [listResources])
}
